import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Play, Sparkles, Star, ShieldCheck, Brain, Target, Rocket, Lightbulb,
  Scale, Gauge, Layers, Check, X as XIcon, Quote, Clock, ChevronDown, Globe2,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Reveal, SectionHeading, Eyebrow } from "../components/common";
import VideoModal from "../components/VideoModal";
import Seo from "../components/Seo";
import {
  stats, assessments, steps, comparison, abilities, credentials, testimonials, faqs, videos, heroImages,
} from "../mock";

const iconMap = { Brain, Target, Rocket, Lightbulb, Scale, Gauge, Sparkles, Layers };

function MarqueeStrip() {
  const items = ["Discover Natural Abilities", "Nursery to Grade 12", "Focus + Intellect + Creativity", "Advanced Cognitive Science", "Instant Cognitive Profile", "World's Most Advanced Assessments"];
  const row = [...items, ...items];
  return (
    <div className="relative overflow-hidden border-y border-violet-100 bg-white py-4">
      <div className="flex w-max animate-marquee gap-10">
        {row.map((t, i) => (
          <span key={i} className="flex items-center gap-3 text-[16px] font-semibold text-slate-500 whitespace-nowrap">
            <Sparkles className="h-4 w-4 text-violet-500" /> {t}
          </span>
        ))}
      </div>
    </div>
  );
}

export default function Home() {
  const [openFaq, setOpenFaq] = useState(0);
  const [video, setVideo] = useState(null);

  return (
    <div className="overflow-hidden">
      <Seo
        title="MapMyKid | Discover & Develop the True Potential of your Child"
        description="MapMyKid helps parents, schools and educators discover a child's natural abilities, learning speed and mindset through advanced cognitive science assessments for Nursery to Grade 12, with an instant cognitive profile."
      />
      {/* HERO */}
      <section className="relative hero-gradient pt-28 sm:pt-32 lg:pt-40 pb-16 sm:pb-20">
        <div className="absolute inset-0 grain" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center">
            <div className="animate-fade-up">
              <Eyebrow>Advanced Cognitive Science</Eyebrow>
              <h1 className="mt-6 text-[28px] sm:text-[52px] lg:text-[60px] font-extrabold leading-[1.04] text-slate-900">
                Discover &amp; Develop the{" "}
                <span className="brand-gradient-text">True Potential</span> of your Child.
              </h1>
              <p className="mt-6 text-[18px] sm:text-[19px] leading-relaxed text-slate-600 max-w-xl">
                Assessing your child at the right age and the right time helps you discover their natural abilities early, and develop them fast, for a super successful life and a sharp, confident mindset.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3.5">
                <a href="#assessments">
                  <Button className="cta-gradient hover:opacity-95 text-white rounded-full h-14 px-7 text-[17px] font-semibold shadow-xl shadow-violet-300/50 w-full sm:w-auto">
                    Learn More <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </a>
                <Button
                  onClick={() => setVideo(videos[0])}
                  variant="outline"
                  className="rounded-full h-14 px-7 text-[17px] font-semibold border-violet-200 text-violet-700 hover:bg-violet-50 w-full sm:w-auto"
                >
                  <Play className="mr-2 h-5 w-5" /> See how it works
                </Button>
              </div>
              <div className="mt-8 flex items-center gap-3 text-[15px] text-slate-500">
                <div className="flex -space-x-2">
                  {testimonials.map((t) => (
                    <img key={t.name} src={t.avatar} alt={t.name} className="h-9 w-9 rounded-full ring-2 ring-white object-cover" />
                  ))}
                </div>
                <div className="flex items-center gap-1 text-amber-500">
                  {[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4 fill-amber-400 text-amber-400" />)}
                  <span className="ml-1 text-slate-600 font-semibold">Loved by parents & schools</span>
                </div>
              </div>
            </div>

            <div className="relative animate-fade-up" style={{ animationDelay: "120ms" }}>
              <div className="relative rounded-[28px] overflow-hidden shadow-2xl shadow-violet-300/40 ring-1 ring-violet-100">
                <img src={heroImages.main} alt="Indian parent and child taking an online assessment at home" className="h-[420px] sm:h-[480px] w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-fuchsia-900/40 via-transparent to-transparent" />
              </div>
              <div className="absolute -left-3 sm:-left-6 top-10 glass-card rounded-2xl px-4 py-3 shadow-xl animate-floaty">
                <p className="text-[13px] font-semibold text-fuchsia-600">Assessment Complete</p>
                <p className="text-[16px] font-bold text-slate-900">Aarav, Grade 5</p>
                <p className="text-[14px] text-slate-500">Cognitive Profile ready</p>
              </div>
              <div className="absolute -right-2 sm:-right-5 bottom-10 glass-card rounded-2xl px-5 py-4 shadow-xl animate-floaty" style={{ animationDelay: "1.4s" }}>
                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-6">
                    <span className="text-[14px] font-medium text-slate-500">Focus</span>
                    <span className="text-[22px] font-extrabold brand-gradient-text">107</span>
                  </div>
                  <div className="flex items-center justify-between gap-6">
                    <span className="text-[14px] font-medium text-slate-500">Natural Ability</span>
                    <span className="text-[18px] font-extrabold brand-gradient-text">Visual</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="mt-16 grid grid-cols-2 lg:grid-cols-4 gap-4">
            {stats.map((s, i) => (
              <Reveal key={s.label} delay={i * 80}>
                <div className="rounded-2xl bg-white/70 backdrop-blur border border-violet-100 p-5 sm:p-6 text-center card-hover">
                  <p className="text-[22px] sm:text-[34px] font-extrabold brand-gradient-text">{s.value}</p>
                  <p className="mt-1 text-[15px] font-medium text-slate-500">{s.label}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <MarqueeStrip />

      {/* ASSESSMENTS */}
      <section id="assessments" className="section-pad bg-white scroll-mt-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Assessments By Age" title="Give your child the biggest advantage for lifetime." subtitle="Each 60 minute assessment is tuned to the developmental stage of the child: what to measure, who answers, and how to grow from here." />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {assessments.map((a, i) => (
              <Reveal key={a.id} delay={i * 100}>
                <div className="group relative flex flex-col h-full rounded-3xl border border-violet-100 bg-white overflow-hidden card-hover">
                  <span className="absolute right-4 top-4 z-10 rounded-full bg-white/95 px-3 py-1 text-[14px] font-bold text-violet-700 shadow-lg">{a.price} / assessment</span>
                  <div className="relative h-44 overflow-hidden">
                    <img src={a.image} alt={a.title} className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/50 to-transparent" />
                    <div className="absolute bottom-3 left-4">
                      <p className="text-[14px] font-semibold text-white/90">{a.tag}</p>
                      <p className="text-[14px] text-white/70">{a.ages}</p>
                    </div>
                  </div>
                  <div className="flex flex-col flex-1 p-6">
                    <h3 className="text-[22px] font-bold text-slate-900">{a.title}</h3>
                    <p className="mt-2 text-[16px] text-slate-600 leading-relaxed">{a.summary}</p>
                    <span className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-teal-50 px-3 py-1.5 text-[14px] font-semibold text-teal-700"><Sparkles className="h-4 w-4" /> {a.worksheets}</span>
                    <ul className="mt-4 space-y-2.5 flex-1">
                      {a.indices.slice(0, 4).map((ix) => (
                        <li key={ix} className="flex items-start gap-2.5 text-[16px] text-slate-700">
                          <Check className="mt-0.5 h-5 w-5 shrink-0 text-teal-500" /> {ix}
                        </li>
                      ))}
                    </ul>
                    <div className="mt-5 flex items-center justify-between border-t border-violet-100 pt-4">
                      <span className="flex items-center gap-1.5 text-[15px] font-medium text-slate-500"><Clock className="h-4 w-4" /> {a.duration}</span>
                      <Link to="/assessments" className="inline-flex items-center gap-1 text-[16px] font-semibold text-violet-700 hover:gap-2 transition-all">
                        View details <ArrowRight className="h-4 w-4" />
                      </Link>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ABILITIES */}
      <section className="section-pad bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Abilities We Measure" title="The mindset we actually measure." />
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {abilities.map((ab, i) => {
              const Icon = iconMap[ab.icon] || Brain;
              return (
                <Reveal key={ab.title} delay={(i % 4) * 80}>
                  <div className="group h-full rounded-3xl border border-violet-100 bg-white p-6 card-hover">
                    <span className="grid h-13 w-13 h-14 w-14 place-items-center rounded-2xl bg-violet-50 text-violet-600 transition-colors group-hover:cta-gradient group-hover:text-white">
                      <Icon className="h-7 w-7" />
                    </span>
                    <h3 className="mt-4 text-[18px] font-bold text-slate-900">{ab.title}</h3>
                    <p className="mt-2 text-[16px] text-slate-600 leading-relaxed">{ab.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* DISCOVER FREE BANNER (gradient black) */}
      <section className="bg-white py-12 sm:py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Link to="/discover" data-testid="home-discover-banner" className="group block relative overflow-hidden rounded-[28px] p-6 sm:p-10 shadow-2xl shadow-black/30" style={{ background: "linear-gradient(135deg,#0b0713 0%,#1b1030 55%,#2a1250 100%)" }}>
            <div className="absolute inset-0 grain opacity-40" />
            <div className="absolute -right-16 -top-16 h-64 w-64 rounded-full bg-violet-600/25 blur-3xl" />
            <div className="absolute -left-16 -bottom-16 h-64 w-64 rounded-full bg-fuchsia-500/20 blur-3xl" />
            <div className="relative flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
              <div>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 border border-white/15 px-3.5 py-1.5 text-[13px] font-bold text-teal-300"><Sparkles className="h-4 w-4" /> Free · No signup · 10 minutes</span>
                <h2 className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white leading-tight">Discover how your child <span className="brand-gradient-text">naturally learns.</span></h2>
                <p className="mt-3 text-[16px] text-white/70 max-w-xl">A quick, playful activity for students in Grades 5 to 8. Get an instant visual strengths report, free.</p>
              </div>
              <span className="shrink-0 inline-flex items-center gap-2 rounded-full cta-gradient text-white px-7 h-14 text-[16px] font-bold shadow-lg shadow-violet-500/30 group-hover:gap-3 transition-all">
                Start Free <ArrowRight className="h-5 w-5" />
              </span>
            </div>
          </Link>
        </div>
      </section>

      {/* CREDENTIALS */}
      <section className="section-pad soft-gradient">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Global Credentials" title="Research that has travelled the world." subtitle="Our methodology, scientific validity and evidence of effectiveness have been presented, published and appreciated across the USA, UK, Europe and India." />
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {credentials.map((c, i) => (
              <Reveal key={c.title} delay={i * 70}>
                <div className="h-full rounded-3xl border border-violet-100 bg-white p-6 card-hover">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-teal-50 px-3 py-1 text-[14px] font-semibold text-teal-700"><Globe2 className="h-3.5 w-3.5" /> {c.badge}</span>
                  <h3 className="mt-4 text-[18px] font-bold text-slate-900 leading-snug">{c.title}</h3>
                  <p className="mt-1.5 text-[15px] text-slate-500">{c.place}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="section-pad soft-gradient">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="How MapMyKid Works" title="From assessment to action in four simple steps." subtitle="A clear journey: no waiting rooms, no jargon. Every step is designed with the child, the parent and the science in mind." />
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((s, i) => (
              <Reveal key={s.no} delay={i * 90}>
                <div className="relative h-full rounded-3xl bg-white border border-violet-100 p-6 card-hover">
                  <span className="text-[40px] font-extrabold text-violet-100">{s.no}</span>
                  <h3 className="mt-2 text-[19px] font-bold text-slate-900">{s.title}</h3>
                  <p className="mt-2 text-[16px] text-slate-600 leading-relaxed">{s.desc}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-violet-50 px-3 py-1 text-[14px] font-semibold text-violet-700"><Clock className="h-3.5 w-3.5" /> {s.meta}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* VIDEOS */}
      <section className="section-pad bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Watch & Learn" title="Understand the assessment in 2 minutes." subtitle="A quick walkthrough of exactly how the MapMyKid assessment works, from start to your child's cognitive profile." />
          <div className="mt-14 mx-auto max-w-3xl">
            {videos.map((v, i) => (
              <Reveal key={v.title} delay={i * 100}>
                <button onClick={() => setVideo(v)} className="group relative w-full text-left rounded-3xl overflow-hidden border border-violet-100 card-hover">
                  <div className="relative h-64 sm:h-80">
                    <img src={v.poster} alt={v.title} className="h-full w-full object-cover" />
                    <div className="absolute inset-0 bg-slate-900/35 group-hover:bg-slate-900/25 transition-colors" />
                    <span className="absolute inset-0 grid place-items-center">
                      <span className="grid h-20 w-20 place-items-center rounded-full bg-white/90 text-violet-700 shadow-xl transition-transform group-hover:scale-110">
                        <Play className="h-8 w-8 fill-violet-700" />
                      </span>
                    </span>
                  </div>
                  <div className="p-6 bg-white">
                    <h3 className="text-[20px] font-bold text-slate-900">{v.title}</h3>
                    <p className="mt-1 text-[16px] text-slate-600">{v.desc}</p>
                  </div>
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="section-pad bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Parents & Schools" title="Trusted across homes and classrooms." subtitle="Parents and educators across India rely on MapMyKid to understand how their children truly think, learn and grow." />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <Reveal key={t.name} delay={i * 100}>
                <div className="relative h-full rounded-3xl border border-violet-100 bg-white p-7 card-hover">
                  <Quote className="h-9 w-9 text-violet-200" />
                  <p className="mt-3 text-[17px] leading-relaxed text-slate-700">{t.quote}</p>
                  <div className="mt-6 flex items-center gap-3">
                    <img src={t.avatar} alt={t.name} className="h-12 w-12 rounded-full object-cover ring-2 ring-violet-100" />
                    <div>
                      <p className="text-[16px] font-bold text-slate-900">{t.name}</p>
                      <p className="text-[15px] text-slate-500">{t.role}</p>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-pad soft-gradient">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Frequently Asked" title="Everything parents ask us." />
          <div className="mt-12 space-y-3">
            {faqs.map((f, i) => (
              <div key={i} className="rounded-2xl border border-violet-100 bg-white overflow-hidden">
                <button onClick={() => setOpenFaq(openFaq === i ? -1 : i)} className="flex w-full items-center justify-between gap-4 p-5 text-left">
                  <span className="text-[17px] font-semibold text-slate-900">{f.q}</span>
                  <ChevronDown className={`h-5 w-5 shrink-0 text-violet-600 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
                </button>
                <div className={`grid transition-all duration-300 ${openFaq === i ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
                  <div className="overflow-hidden">
                    <p className="px-5 pb-5 text-[16px] leading-relaxed text-slate-600">{f.a}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-pad bg-white">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="relative overflow-hidden rounded-[32px] cta-gradient px-6 py-14 sm:px-14 sm:py-16 text-center">
              <div className="absolute inset-0 grain opacity-70" />
              <div className="absolute -top-20 -right-16 h-64 w-64 rounded-full bg-white/10 blur-2xl" />
              <div className="relative">
                <ShieldCheck className="mx-auto h-10 w-10 text-white/90" />
                <h2 className="mt-4 text-[30px] sm:text-[40px] font-extrabold text-white leading-tight">Discover what your child is wired for.</h2>
                <p className="mx-auto mt-4 max-w-2xl text-[18px] text-white/85">Book an assessment for your child, or bring MapMyKid to your school. It takes an hour, the clarity lasts a lifetime.</p>
                <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3.5">
                  <Link to="/book"><Button className="bg-white text-violet-700 hover:bg-violet-50 rounded-full h-14 px-8 text-[17px] font-bold w-full sm:w-auto">Start Assessment <ArrowRight className="ml-2 h-5 w-5" /></Button></Link>
                  <Link to="/partner"><Button variant="outline" className="border-white/60 bg-white/10 text-white hover:bg-white/20 hover:text-white rounded-full h-14 px-8 text-[17px] font-bold w-full sm:w-auto">Partner With Us</Button></Link>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <VideoModal open={!!video} onClose={() => setVideo(null)} video={video} />
    </div>
  );
}
